import React from "react";

interface HomeHeaderProps {
  userName: string;
  companyName: string;
  totalOpenItems: number;
  /** Injectable for tests; defaults to the viewer's local clock. */
  now?: Date;
}

/**
 * Time-of-day greeting.
 *
 * Computed from the browser clock, not the server. The API's `as_of` is the
 * accounting date the counts were run against, which for demo tenants is
 * deliberately pinned to a past date — using it here would greet someone with
 * "Good evening" at 9am.
 */
const greetingFor = (now: Date): string => {
  const hour = now.getHours();

  if (hour < 12) {
    return "Good morning";
  }

  if (hour < 17) {
    return "Good afternoon";
  }

  return "Good evening";
};

const summaryLine = (totalOpenItems: number, companyName: string): string => {
  const scope = companyName ? ` across ${companyName}` : "";

  if (totalOpenItems === 0) {
    return `You're all caught up${scope} today.`;
  }

  const noun = totalOpenItems === 1 ? "item" : "items";

  return `You've got ${totalOpenItems} ${noun} to review${scope} today.`;
};

export const HomeHeader: React.FC<HomeHeaderProps> = ({
  userName,
  companyName,
  totalOpenItems,
  now,
}) => {
  const greeting = greetingFor(now ?? new Date());

  return (
    <header className="flex w-full flex-col items-start gap-2">
      <h1 className="text-heading-1 font-heading-1 text-default-font">
        {greeting}
        {userName ? `, ${userName}` : ""}
      </h1>
      <p className="text-body font-body text-subtext-color">
        {summaryLine(totalOpenItems, companyName)}
      </p>
    </header>
  );
};
